import { useState, type FormEvent } from "react";
import { Check, ExternalLink, LogOut, Mail, Sparkles } from "lucide-react";
import {
  createEmailAccount,
  resetEmailPassword,
  signInWithApple,
  signInWithEmail,
  signInWithGoogle,
  signOutCurrentUser,
} from "../../infrastructure/firebase/auth-service";
import { useAuth } from "../providers/AuthProvider";
import { Dialog } from "./Dialog";

interface AuthDialogProps {
  open: boolean;
  onClose: () => void;
}

export function AuthDialog({ open, onClose }: AuthDialogProps) {
  const { user, entitlement } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState<"signin" | "create">("signin");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const run = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setMessage(null);
    try {
      await action();
      onClose();
    } catch (reason) {
      setMessage(reason instanceof Error ? reason.message : "Authentication could not be completed.");
    } finally {
      setBusy(false);
    }
  };

  const submitEmail = (event: FormEvent) => {
    event.preventDefault();
    void run(() => mode === "signin"
      ? signInWithEmail(email, password)
      : createEmailAccount(email, password));
  };

  if (user) {
    return (
      <Dialog open={open} title="Account" onClose={onClose} footer={(
        <>
          <span className="supporting-text">Cloud data is scoped to this account.</span>
          <button className="btn btn-danger" onClick={() => void run(signOutCurrentUser)} type="button">
            <LogOut size={15} /> Sign out
          </button>
        </>
      )}>
        <div className="account-summary">
          <div className="account-avatar account-avatar-large">{initials(user.displayName ?? user.email)}</div>
          <div>
            <h3>{user.displayName || "LogBookAnaes user"}</h3>
            <p className="supporting-text">{user.email || "Private Apple relay account"}</p>
            <span className={entitlement === "pro" ? "badge badge-mint" : "badge"}>
              {entitlement === "pro" ? "Pro" : entitlement === "lapsed" ? "Pro ended · read-only" : "Free"}
            </span>
          </div>
        </div>
        <section className="account-plan-card" data-plan={entitlement}>
          <div className="account-plan-heading">
            <span className="list-icon"><Sparkles size={16} /></span>
            <div>
              <h4>{entitlement === "pro" ? "Your Pro evidence workspace" : entitlement === "lapsed" ? "Renew full Studio access" : "Make the logbook do the admin"}</h4>
              <p>{entitlement === "pro" ? "The same Pro account works across LogBookAnaes and Studio." : entitlement === "lapsed" ? "Your existing cloud data remains available for review and export, but changes require active Pro access." : "Pro is designed around the jobs that otherwise take time at ARCP and portfolio deadlines."}</p>
            </div>
          </div>
          <ul className="account-plan-benefits">
            <li><Check size={14} /> Cloud case management and bulk correction</li>
            <li><Check size={14} /> One Tap cases and synced templates</li>
            <li><Check size={14} /> ARCP PDF packs and reusable report layouts</li>
          </ul>
          <a className="btn btn-secondary" href="https://app.logbookanaes.com" rel="noreferrer" target="_blank">
            {entitlement === "pro" ? "Manage Pro in LogBookAnaes" : entitlement === "lapsed" ? "Renew Pro in LogBookAnaes" : "See Pro in LogBookAnaes"} <ExternalLink size={14} />
          </a>
        </section>
        {message ? <p className="form-message form-message-error" role="alert">{message}</p> : null}
      </Dialog>
    );
  }

  return (
    <Dialog open={open} title="Sign in to LogBookAnaes" onClose={onClose}>
      <p className="supporting-text auth-intro">
        Use the account already connected to your iPhone or web logbook. Studio never exposes another user&apos;s cases.
      </p>
      <div className="auth-provider-grid">
        <button className="btn btn-secondary" disabled={busy} onClick={() => void run(signInWithApple)} type="button">
          <AppleSignInLogo /> Continue with Apple
        </button>
        <button className="btn btn-secondary" disabled={busy} onClick={() => void run(signInWithGoogle)} type="button">
          <GoogleSignInLogo /> Continue with Google
        </button>
      </div>
      <div className="auth-divider"><span>or use email</span></div>
      <form className="auth-form" onSubmit={submitEmail}>
        <div className="field">
          <label htmlFor="auth-email">Email</label>
          <input autoComplete="email" className="input" id="auth-email" onChange={(event) => setEmail(event.target.value)} required type="email" value={email} />
        </div>
        <div className="field">
          <label htmlFor="auth-password">Password</label>
          <input autoComplete={mode === "signin" ? "current-password" : "new-password"} className="input" id="auth-password" minLength={6} onChange={(event) => setPassword(event.target.value)} required type="password" value={password} />
        </div>
        {message ? <p className="form-message form-message-error" role="alert">{message}</p> : null}
        <button className="btn btn-primary" disabled={busy} type="submit">
          <Mail size={15} /> {mode === "signin" ? "Sign in" : "Create account"}
        </button>
      </form>
      <div className="auth-links">
        <button className="link-button" onClick={() => setMode((value) => value === "signin" ? "create" : "signin")} type="button">
          {mode === "signin" ? "Create an email account" : "I already have an account"}
        </button>
        {mode === "signin" ? (
          <button className="link-button" disabled={!email || busy} onClick={() => void run(async () => {
            await resetEmailPassword(email);
            setMessage("Password reset email sent.");
          })} type="button">Reset password</button>
        ) : null}
      </div>
    </Dialog>
  );
}

function initials(value: string | null): string {
  if (!value) return "LB";
  return value.split(/\s+|@/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join("") || "LB";
}

function AppleSignInLogo() {
  return (
    <svg aria-hidden="true" className="auth-provider-logo auth-provider-logo-apple" focusable="false" viewBox="0 0 24 24">
      <path
        d="M16.36 1.43c0 1.1-.41 2.13-1.22 3.05-.98 1.1-2.17 1.73-3.46 1.63-.15-1.06.43-2.18 1.19-3.02.83-.93 2.28-1.64 3.49-1.66ZM20.5 17.43c-.52 1.2-.77 1.73-1.44 2.8-.93 1.42-2.24 3.2-3.87 3.22-1.45.01-1.82-.94-3.79-.93-1.97.01-2.38.95-3.83.94-1.63-.02-2.88-1.62-3.81-3.04-2.6-3.98-2.88-8.66-1.27-11.15 1.14-1.76 2.94-2.79 4.64-2.79 1.73 0 2.82.95 4.25.95 1.39 0 2.24-.95 4.25-.95 1.52 0 3.13.83 4.27 2.25-3.75 2.06-3.14 7.41.6 8.7Z"
        fill="currentColor"
      />
    </svg>
  );
}

function GoogleSignInLogo() {
  return (
    <svg aria-hidden="true" className="auth-provider-logo" focusable="false" viewBox="0 0 24 24">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09Z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23Z" fill="#34A853" />
      <path d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84Z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06L5.84 9.9C6.71 7.31 9.14 5.38 12 5.38Z" fill="#EA4335" />
    </svg>
  );
}
